import type { Streak } from '@keel/core';
import { Flame, Snowflake } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui';
import { streakCopy } from '@/copy/engagement';
import { cn } from '@/lib/cn';
import { useAppStore } from '@/lib/store';

export interface StreakChipProps {
  readonly streak: Pick<Streak, 'current' | 'longest' | 'freezeTokens'> | null | undefined;
  /** Number only (top bar); otherwise "5-day streak". */
  readonly compact?: boolean;
  readonly className?: string;
}

/** Daily check-in streak with a popover explaining freeze tokens. Hidden in Calm mode. */
export const StreakChip = ({ streak, compact, className }: StreakChipProps) => {
  const calm = useAppStore((s) => s.calmMode);
  if (calm) return null;
  const current = streak?.current ?? 0;
  const freezes = streak?.freezeTokens ?? 0;
  const active = current > 0;
  if (compact && !active) return null; // the top bar only shows a running streak; Home invites a first one
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex h-8 items-center gap-1.5 rounded-full px-2.5 text-xs font-semibold tabular-nums transition-colors duration-fast coarse:h-11',
            active ? 'bg-accent-soft text-text hover:bg-accent/25' : 'bg-surface-sunken text-text-muted hover:text-text',
            className,
          )}
        >
          <Flame className={cn('size-4', active ? 'text-accent' : 'text-text-muted')} aria-hidden />
          <span aria-hidden>{compact ? streakCopy.short(current) : streakCopy.label(current)}</span>
          <span className="sr-only">{streakCopy.aria(current, freezes)}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="end">
        <p className="font-semibold text-text">{streakCopy.tooltipTitle(current)}</p>
        <p className="mt-2 flex gap-2 text-sm text-text-muted">
          <Snowflake className="mt-0.5 size-4 shrink-0 text-info" aria-hidden />
          <span>{streakCopy.freezeExplainer(freezes)}</span>
        </p>
        {streak && streak.longest > 0 ? <p className="mt-2 text-xs text-text-muted">{streakCopy.longest(streak.longest)}</p> : null}
      </PopoverContent>
    </Popover>
  );
};
